import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { timService } from '../../services/timService';
import { userService } from '../../services/userService';
import { timMessageToThreadMessage } from '../../utils/timAdapters';

export const MessageHoverMenu: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [conversations, setConversations] = useState<any[]>([]);
    const [userProfiles, setUserProfiles] = useState<Record<string, { avatarUrl: string; nickname: string }>>({});
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const fetchConversations = async () => {
        setLoading(true);
        try {
            const convs = await timService.getConversationList();
            const topConvs = convs.slice(0, 5);
            setConversations(topConvs);
            const total = convs.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0);
            setUnreadCount(total);

            // Fetch missing user profiles
            const userIdsToFetch = topConvs.map(conv => {
                return conv.type === 'C2C' ? conv.conversationID.replace('C2C', '') : '';
            }).filter(Boolean);

            if (userIdsToFetch.length > 0) {
                const res = await userService.getAvatarsBatch(userIdsToFetch);
                if (res.success && res.data?.avatars) {
                    setUserProfiles(res.data.avatars);
                }
            }

        } catch (e) {
            console.error('Failed to fetch conversations', e);
        }
        setLoading(false);
    };

    // Keep the unread dot accurate without requiring a hover: fetch once
    // on mount and re-tally whenever TIM reports a conversation change.
    useEffect(() => {
        let active = true;
        const tally = async () => {
            try {
                const convs = await timService.getConversationList();
                if (!active) return;
                setUnreadCount(convs.reduce((sum: number, c: any) => sum + (c.unreadCount || 0), 0));
            } catch { /* ignore */ }
        };
        void tally();
        const unsub = timService.onConversationUpdated(tally);
        return () => { active = false; unsub?.(); };
    }, []);

    const handleMouseEnter = () => {
        setIsOpen(true);
        fetchConversations();
    };

    const handleMouseLeave = () => {
        setIsOpen(false);
    };

    return (
        <div 
            className="relative py-2" 
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <button 
                onClick={() => navigate('/messages')} 
                className="group relative rounded-full p-2 transition-colors hover:bg-gray-100"
            >
                <Mail className={`h-5 w-5 text-gray-600 transition-colors group-hover:text-black ${unreadCount > 0 ? 'animate-attention-pulse text-blue-600' : ''}`} />
                {unreadCount > 0 && (
                    <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-blue-500"></span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 top-full pt-2 w-80 z-50">
                    <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                            <h3 className="font-semibold text-gray-900 text-sm">最近消息</h3>
                            <button
                                onClick={() => navigate('/messages')}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                            >
                                查看全部
                            </button>
                        </div>

                        <div className="max-h-[300px] overflow-y-auto">
                            {loading ? (
                                <div className="flex justify-center py-8">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black"></div>
                                </div>
                            ) : conversations.length === 0 ? (
                                <div className="py-10 text-center text-gray-400 text-sm">暂无消息</div>
                            ) : (
                                conversations.map((conv) => {
                                    const partnerId = conv.type === 'C2C' ? conv.conversationID.replace('C2C', '') : '';
                                    const profile = userProfiles[partnerId];
                                    const nickname = profile?.nickname || conv.userProfile?.nick || partnerId || '未知用户';
                                    const avatar = profile?.avatarUrl || conv.userProfile?.avatar;
                                    const msgObj = conv.lastMessage ? timMessageToThreadMessage(conv.lastMessage) : null;
                                    const contentPreview = msgObj?.content || conv.lastMessage?.messageForShow || '';

                                    return (
                                        <div
                                            key={conv.conversationID}
                                            onClick={() => navigate(`/messages/${conv.conversationID}`)}
                                            className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                                    {avatar ? (
                                                        <img src={avatar} alt="avatar" className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-sm">
                                                            {nickname.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-center mb-1">
                                                        <p className="text-sm font-semibold text-gray-900 truncate">
                                                            {nickname}
                                                        </p>
                                                        {conv.unreadCount > 0 && (
                                                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {contentPreview}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
